/** tm/legal/LegalDashboardCard.tsx — 대시보드 맨 위 「오늘의 새 소식」: 가장 중요한 1건을 크게(법령 개정 > 국회 법안 > 입법예고 > 판례·해석) + 나머지 4건.
 *  업무관리 셸이 받아 둔 /alerts 값을 같이 쓴다 (추가 호출 없음). 뉴스 수만 /digest 에서. */
import { useEffect, useState, type KeyboardEvent } from 'react';
import { C, T, R } from '../components/ui';
import { getDigest } from './legalApi';
import { whenLabel, MUTED } from './legalTheme';
import { SourceBadge, TopicTag, ItemTitle, useWindowWidth } from './legalUi';
import { useAlertsSnapshot } from './useLegalAlerts';
import { entries, ranked, badgeOf, hookOf, hookColor, navOf, groupCount, type AlertEntry } from './alertUtil';
import { useDashboardCardMounted } from './useNewsPopup';
import type { AlertKey, LegalAlerts, LegalNavParams } from './legalTypes';

const GROUPS: [AlertKey, string][] = [['law', '법령 개정'], ['bill', '국회 법안'], ['cabinet', '국무회의'], ['notice', '입법예고'], ['legal', '판례·해석']];

interface Props { onOpen: (p?: LegalNavParams) => void }

const keyOpen = (fn: () => void) => (e: KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fn(); } };

function Top({ e, d, onOpen }: { e: AlertEntry; d: LegalAlerts; onOpen: () => void }) {
  const b = badgeOf(e);
  return (
    <div role="button" tabIndex={0} onClick={onOpen} onKeyDown={keyOpen(onOpen)} className="lw-dash-row" style={{
      padding: '12px 14px', cursor: 'pointer', background: C.bgAlt, display: 'flex', flexDirection: 'column', gap: 5, minWidth: 0,
    }}>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ fontSize: T.sm, fontWeight: 700, color: C.accent }}>가장 중요</span>
        <SourceBadge group={b.group} label={b.label} />
        {e.it.topic && <TopicTag>{e.it.topic}</TopicTag>}
        <span style={{ marginLeft: 'auto', fontSize: T.sm, color: MUTED, fontVariantNumeric: 'tabular-nums' }}>{whenLabel(e.it.first_seen_at)}</span>
      </div>
      <div className="lw-clamp" style={{ fontSize: T.lg, fontWeight: 700, color: C.text, lineHeight: 1.45 }}><ItemTitle title={e.it.title} custom={e.it.custom_title} /></div>
      <div style={{ fontSize: T.sm, color: hookColor(e, C.accent, C.sub) }}>{hookOf(e)}</div>
      <span style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 2 }}>
        <span style={{ fontSize: T.sm, color: C.white, background: C.accent, borderRadius: R.sm, padding: '4px 10px', fontWeight: 600 }}>바로 보기</span>
        <span style={{ fontSize: T.sm, color: MUTED, fontVariantNumeric: 'tabular-nums' }}>
          {GROUPS.map(([k, l]) => [l, groupCount(d, k)] as const).filter(([, n]) => n > 0).map(([l, n]) => `${l} ${n}`).join(' · ')}
        </span>
      </span>
    </div>
  );
}

function Line({ e, onOpen }: { e: AlertEntry; onOpen: () => void }) {
  const b = badgeOf(e);
  return (
    <div role="button" tabIndex={0} onClick={onOpen} onKeyDown={keyOpen(onOpen)} className="lw-dash-row" style={{
      padding: '8px 14px', cursor: 'pointer', display: 'flex', gap: 8, alignItems: 'center', borderBottom: `1px solid ${C.border}`, minHeight: 40,
    }}>
      <SourceBadge group={b.group} label={b.label} />
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: T.sm, fontWeight: 600, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}><ItemTitle title={e.it.title} custom={e.it.custom_title} /></span>
        <span style={{ display: 'block', fontSize: T.sm, color: MUTED, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.it.topic ? `${e.it.topic} · ` : ''}{hookOf(e)}</span>
      </span>
      <span style={{ color: MUTED, fontSize: T.md + 1 }}>›</span>
    </div>
  );
}

export default function LegalDashboardCard({ onOpen }: Props) {
  useDashboardCardMounted();
  const { data, loaded } = useAlertsSnapshot();
  const w = useWindowWidth();
  const wide = w >= 1100;
  const [news, setNews] = useState<number | null>(null);
  useEffect(() => {
    let alive = true;
    getDigest().then(d => { if (alive) setNews(d ? d.news ?? d.topics.reduce((s, t) => s + (t.news || 0), 0) : null); }, () => undefined);
    return () => { alive = false; };
  }, []);

  const list = ranked(entries(data), data);
  const [top, ...rest] = list;
  const color = C.accent; // 빨강은 새 소식 수(사이드바·팝업·필터)와 마감 임박에만
  return (
    <div style={{ background: C.white, borderRadius: R.lg, border: `2px solid ${color}33`, overflow: 'hidden', marginBottom: 14 }}>
      <style>{`.lw-dash-row:hover{background:${C.bgAlt}!important}.lw-dash-row:focus-visible{outline:2px solid ${C.accent};outline-offset:-2px}`}</style>
      <div style={{ padding: '10px 14px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <span style={{ fontSize: T.md, fontWeight: 700 }}>📚 오늘의 새 소식</span>
        <span style={{ padding: '2px 7px', borderRadius: R.sm, background: color + '22', color, fontSize: T.sm, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{loaded ? data.total : '…'}건</span>
        {w >= 700 && <span style={{ fontSize: T.sm, color: MUTED }}>법령 개정·판례·해석·국회 법안·입법예고{news ? ` · 뉴스 ${news}건은 알림 없이 쌓임` : ''}</span>}
        <button type="button" onClick={() => onOpen(data.total ? { topic: '', alerts: true } : undefined)} style={{ marginLeft: 'auto', border: 0, background: 'transparent', color: C.accent, fontSize: T.sm, fontWeight: 600, cursor: 'pointer', padding: '4px 0' }}>
          {data.total ? '전체 보기 ›' : '법령·판례·뉴스 열기 ›'}
        </button>
      </div>
      {!loaded && <div aria-hidden="true" style={{ padding: 14 }}><div className="lw-skel" style={{ height: 14, width: '60%', borderRadius: 4, background: C.bg }} /></div>}
      {loaded && !top && <div style={{ padding: '14px', textAlign: 'center', color: MUTED, fontSize: T.sm }}>새로 확인할 소식이 없습니다 · 매일 새벽 4시에 새로 모읍니다</div>}
      {top && (
        <div style={{ display: 'grid', gridTemplateColumns: wide && rest.length ? 'minmax(0,1fr) minmax(0,1fr)' : 'minmax(0,1fr)' }}>
          <Top e={top} d={data} onOpen={() => onOpen(navOf(top))} />
          {rest.length > 0 && (
            <div style={{ borderLeft: wide ? `1px solid ${C.border}` : 0, borderTop: wide ? 0 : `1px solid ${C.border}` }}>
              {rest.slice(0, 3).map(e => <Line key={`${e.key}${e.it.id}`} e={e} onOpen={() => onOpen(navOf(e))} />)}
              {data.total > 4 && (
                <button type="button" onClick={() => onOpen({ topic: '', alerts: true })} style={{ width: '100%', padding: '7px 0', border: 'none', background: 'transparent', color: C.accent, fontSize: T.sm, fontWeight: 600, cursor: 'pointer' }}>
                  전체보기 ({data.total}건)
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
